import type { Page, Pagination } from '@hello-aria/api-client'

/** makalu-api answers a list as an array and puts its pagination in these headers. */
export const makaluPaginationHeaders = {
    hasNextPage: 'x-has-next-page',
    hasPrevPage: 'x-has-prev-page',
    totalCount: 'x-total-count',
    startCursor: 'x-start-cursor',
    endCursor: 'x-end-cursor'
} as const satisfies Record<keyof Pagination, string>

// A missing or malformed count stays undefined, so no screen shows NaN (ERR-7).
const toCount = (value: string | null): number | undefined => {
    if (value === null || value.trim() === '') return undefined
    const count = Number(value)

    return Number.isFinite(count) ? count : undefined
}

export const toPagination = (headers: Headers): Pagination => ({
    hasNextPage: headers.get(makaluPaginationHeaders.hasNextPage) === 'true',
    hasPrevPage: headers.get(makaluPaginationHeaders.hasPrevPage) === 'true',
    totalCount: toCount(headers.get(makaluPaginationHeaders.totalCount)),
    startCursor: headers.get(makaluPaginationHeaders.startCursor) ?? undefined,
    endCursor: headers.get(makaluPaginationHeaders.endCursor) ?? undefined
})

export const toPage = <Item>(body: Item[], headers: Headers): Page<Item> => ({
    body,
    pagination: toPagination(headers)
})

/** The headers makalu-api sends with a list, for a test that answers one. */
export const toPaginationHeaders = ({
    hasNextPage,
    hasPrevPage,
    totalCount,
    startCursor,
    endCursor
}: Pagination): Record<string, string> => {
    const entries: [string, boolean | number | string | undefined][] = [
        [makaluPaginationHeaders.hasNextPage, hasNextPage],
        [makaluPaginationHeaders.hasPrevPage, hasPrevPage],
        [makaluPaginationHeaders.totalCount, totalCount],
        [makaluPaginationHeaders.startCursor, startCursor],
        [makaluPaginationHeaders.endCursor, endCursor]
    ]

    return Object.fromEntries(
        entries.flatMap(([header, value]) => (value === undefined ? [] : [[header, String(value)]]))
    )
}
